import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageTitle from '../components/PageTitle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GeneralTab from './settings/GeneralTab';
import SnowflakeConnectionTab from './settings/SnowflakeConnectionTab';
import DbtBuildsTab from './settings/DbtBuildsTab';
import WhiteLabelTab from './settings/WhiteLabelTab';
import EllieTab from './settings/EllieTab';
import AnalyticsTab from './settings/AnalyticsTab';
import SubtenantsTab from './settings/SubtenantsTab';
import OnboardingTab from './settings/OnboardingTab';
import TenantConfigService from '@/services/tenantConfig.service';

const VALID_TABS = ['general', 'snowflake', 'dbt-builds', 'white-label', 'ellie', 'analytics', 'onboarding', 'subtenants'];

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? '';
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'general';

  const setActiveTab = (tab: string) => {
    setSearchParams(tab === 'general' ? {} : { tab }, { replace: true });
  };

  const { data: registrationData } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: () => TenantConfigService.getInstance().getTenantRegistration(),
    staleTime: 5 * 60 * 1000,
  });

  const hasHostedAccount = !!registrationData?.tenant_config?.hosted_account;
  const isSubtenant = !!registrationData?.tenantRegistration?.parent_tenant_uuid;
  const showSubtenantsTab = hasHostedAccount && !isSubtenant;

  return (
    <div className="p-8 h-full overflow-auto">
      <PageTitle title="Settings" className="mb-6" />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="snowflake">Snowflake Connection</TabsTrigger>
          <TabsTrigger value="dbt-builds">DBT Builds</TabsTrigger>
          <TabsTrigger value="white-label">White Label</TabsTrigger>
          <TabsTrigger value="ellie">Ellie</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          {showSubtenantsTab && (
            <TabsTrigger value="subtenants">Subtenants</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <GeneralTab />
        </TabsContent>

        <TabsContent value="snowflake" className="mt-6">
          <SnowflakeConnectionTab />
        </TabsContent>

        <TabsContent value="dbt-builds" className="mt-6">
          <DbtBuildsTab />
        </TabsContent>

        <TabsContent value="white-label" className="mt-6">
          <WhiteLabelTab />
        </TabsContent>

        <TabsContent value="ellie" className="mt-6">
          <EllieTab />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-6">
          <OnboardingTab />
        </TabsContent>

        {showSubtenantsTab && (
          <TabsContent value="subtenants" className="mt-6">
            <SubtenantsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
